import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "styled-components";
import {
  Box,
  Stack,
  Cluster,
  Module,
  Heading,
  Paragraph,
  ButtonWithAction,
  WalletBadgeIcon,
  AutopayWalletIcon,
  Modal,
  constants
} from "@thecb/components";
import { SHADOW_BASE_STANDARD } from "../../../../../../constants/style_constants";
import {
  getIsAutopayForPayment,
  getPaidWorkflowHasAutopay,
  getFirstPaidObligationAssociationId
} from "../../Payment.selectors";
import {
  getHasSavedPaymentMethodAndAddress,
  getObligationAssociationById
} from "../../../../../profile/pages/user-profile/Profile.selectors";
import {
  setDetailedObligation,
  navigateToDetailedObligation,
  PROPERTY_OBLIGATION_TYPE
} from "../../../../../profile/pages/user-profile/Profile.state";
import { PROFILE_KEY } from "../../../../../../util/state";
import { setPostRegistrationRedirect } from "../../../../../../util/postRegistrationRedirect";

export const WalletWidgetCTA = ({
  heading,
  description,
  action,
  buttonText = "Register Now",
  buttonAriaLabel,
  dataQa = "Register Now Widget",
  icon = <WalletBadgeIcon />
}) => {
  const { isMobile } = useContext(ThemeContext);
  const containerGap = isMobile ? "1rem" : "1.5rem";
  const textGap = isMobile ? "0.25rem" : "0.5rem";
  return (
    <Module padding="0" spacing="0" spacingBottom="1.5rem">
      <Box
        background={constants.colors.HINT_GREEN}
        padding={containerGap}
        borderRadius="0.5rem"
        boxShadow={SHADOW_BASE_STANDARD}
        minWidth="100%"
      >
        <Stack childGap={containerGap}>
          <Cluster
            justify="flex-start"
            align="center"
            nowrap
            childGap={containerGap}
          >
            <Box
              padding="0"
              extraStyles={
                isMobile ? `> svg { width: 48px; height: 48px; }` : ""
              }
            >
              {icon}
            </Box>
            <Box
              padding="0"
              extraStyles={`
                flex: 1 1 0;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: ${textGap};
              `}
            >
              <Heading
                variant="h6"
                as="h2"
                weight={constants.fontWeights.FONT_WEIGHT_BOLD}
                color={constants.colors.CHARADE_GREY}
                margin="0"
                extraStyles={
                  isMobile
                    ? "font-size: 0.875rem; line-height: 1.3125rem;"
                    : "letter-spacing: 0.2px;"
                }
              >
                {heading}
              </Heading>
              <Paragraph
                variant={isMobile ? "pS" : "p"}
                weight={constants.fontWeights.FONT_WEIGHT_REGULAR}
                color={constants.colors.BLACK}
                margin="0"
              >
                {description}
              </Paragraph>
            </Box>
          </Cluster>
          <ButtonWithAction
            text={buttonText}
            variant="greenPrimary"
            action={action}
            role="link"
            aria-label={
              buttonAriaLabel ?? `${buttonText}, navigate to next page`
            }
            extraStyles="width: 100%; margin: 0;"
            dataQa={dataQa}
          />
        </Stack>
      </Box>
    </Module>
  );
};

export const WalletRegistrationCTA = ({ isLoggedIn, returnURL }) => {
  const navigate = useNavigate();
  const { metadata } = useContext(ThemeContext);
  const clientDisplayName = metadata?.data?.clientDisplayName ?? "";
  const workflowHasAutopay = useSelector(getPaidWorkflowHasAutopay);

  if (isLoggedIn || workflowHasAutopay) {
    return null;
  }

  return (
    <WalletWidgetCTA
      heading={
        clientDisplayName
          ? `Register for ${clientDisplayName} Wallet`
          : "Register for Wallet"
      }
      description="Next time, use wallet to pay quicker with saved payment methods and billing information"
      action={() => {
        setPostRegistrationRedirect(returnURL);
        navigate("/registration");
      }}
      buttonAriaLabel="Register Now, go to registration"
    />
  );
};

export const AutopayWidgetCTA = ({ isLoggedIn, returnURL }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAutopayEnabled = useSelector(getIsAutopayForPayment);
  const workflowHasAutopay = useSelector(getPaidWorkflowHasAutopay);
  const prereqsMet = useSelector(
    getHasSavedPaymentMethodAndAddress(PROFILE_KEY)
  );
  const [modalOpen, setModalOpen] = useState(false);
  const firstAssocId = useSelector(getFirstPaidObligationAssociationId);
  const liveAssociation = useSelector(
    getObligationAssociationById(PROFILE_KEY)(firstAssocId)
  );
  // refreshObligations can empty the just-paid association post-payment, so
  // we latch the last populated snapshot for the click handler to navigate.
  const matchedAssociationRef = useRef(null);
  useEffect(() => {
    if (liveAssociation?.obligations?.length > 0) {
      matchedAssociationRef.current = liveAssociation;
    }
  }, [liveAssociation]);

  if (!workflowHasAutopay) {
    return null;
  }

  if (!isLoggedIn) {
    return (
      <WalletWidgetCTA
        heading="Never miss a payment"
        description="Register to set up autopay and we'll handle your payments automatically each month."
        buttonText="Register Now"
        action={() => {
          setPostRegistrationRedirect(returnURL);
          navigate("/registration");
        }}
        buttonAriaLabel="Register Now, go to registration"
        dataQa="Sign Up for Autopay Widget"
        icon={<AutopayWalletIcon />}
      />
    );
  }

  // Fail-safe: show only when positively not enrolled. null (undetermined)
  // and true (enrolled) both hide it, so an enrolled user is never prompted.
  if (isAutopayEnabled !== false) {
    return null;
  }

  const getAutopaySetupPath = association => {
    if (association?.config?.type === PROPERTY_OBLIGATION_TYPE) {
      return "/profile/properties/set-up-autopay";
    }
    return "/profile/accounts/set-up-autopay";
  };

  const handleAutopayClick = () => {
    if (!prereqsMet) {
      setModalOpen(true);
      return;
    }
    const matchedAssociation = matchedAssociationRef.current;
    if (matchedAssociation) {
      dispatch(
        setDetailedObligation(
          matchedAssociation.obligations,
          matchedAssociation.config,
          matchedAssociation.id
        )
      );
      dispatch(
        navigateToDetailedObligation(getAutopaySetupPath(matchedAssociation))
      );
    } else {
      dispatch(navigateToDetailedObligation("/profile/accounts"));
    }
  };

  return (
    <>
      <WalletWidgetCTA
        heading="Never miss a payment"
        description="Sign up for autopay and we'll handle your payments automatically each month."
        buttonText="Sign Up for Autopay"
        buttonAriaLabel="Sign Up for Autopay, go to set up autopay"
        action={handleAutopayClick}
        dataQa="Sign Up for Autopay Widget"
        icon={<AutopayWalletIcon />}
      />
      <Modal
        showModal={() => setModalOpen(true)}
        hideModal={() => setModalOpen(false)}
        modalOpen={modalOpen}
        modalHeaderText="Set up autopay"
        modalBodyText="To set up autopay you must save a payment method and address. Do you want to save these now?"
        continueButtonText="Add"
        useDangerButton={false}
        continueAction={() => navigate("/profile/wallet")}
      />
    </>
  );
};
